import {
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip as RechartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART } from "../../lib/designTokens";
import { getMetricVisual } from "../../lib/metricVisuals";
import { ProgressChartContainer } from "./ProgressChartContainer";
import { ProgressChartTooltip } from "./ProgressChartTooltip";
import {
  PROGRESS_AXIS_LINE,
  PROGRESS_CHART_GRID_PROPS,
  PROGRESS_CHART_MARGIN,
  PROGRESS_CHART_TICK,
  PROGRESS_Y_AXIS_WIDTH,
  CHART_MIN_HEIGHT,
  buildNumericDomain,
  formatVolumeMetricValue,
  getXAxisInterval,
} from "./progressChartUtils";

const VOLUME_METRIC_KEYS = {
  distance_m: "distance",
  moving_s: "duration",
  elapsed_s: "duration",
  activity_count: "brand",
  elevation_m: "elevation",
  calories: "calories",
};

export const ProgressVolumeChart = ({ data, metric, unitSystem, chartState, metricLabel }) => {
  const domain = buildNumericDomain(data, [metric], { includeZero: true });
  const latestIndex = data.length - 1;
  const visual = getMetricVisual(VOLUME_METRIC_KEYS[metric] || "neutral");
  const barColor = visual.accent;

  return (
    <div className="app-card-elevated p-4 sm:p-5">
      <ProgressChartContainer
        title="Training volume chart"
        ariaLabel={`Training volume chart showing ${metricLabel || metric} by period`}
        testId="progress-volume-chart"
        chartState={chartState}
        emptyTitle="No volume data for this metric"
        emptyDescription="No grouped periods are available for the selected filters."
        emptyTestId="progress-volume-chart-empty"
        unavailableTitle="No usable volume values"
        unavailableDescription="Periods exist, but this metric has no usable values in the selected range."
        unavailableTestId="progress-volume-chart-unavailable"
        minHeight={CHART_MIN_HEIGHT.volume}
        summary={`Volume chart for ${metricLabel || metric} across ${data.length} periods.`}
      >
        <BarChart data={data} margin={PROGRESS_CHART_MARGIN}>
          <CartesianGrid {...PROGRESS_CHART_GRID_PROPS} />
          <XAxis
            dataKey="label"
            tick={PROGRESS_CHART_TICK}
            tickLine={false}
            axisLine={PROGRESS_AXIS_LINE}
            interval={getXAxisInterval(data.length)}
            minTickGap={8}
          />
          <YAxis
            tick={PROGRESS_CHART_TICK}
            tickLine={false}
            axisLine={PROGRESS_AXIS_LINE}
            width={PROGRESS_Y_AXIS_WIDTH}
            domain={domain}
          />
          <RechartTooltip
            cursor={{ fill: "rgba(127, 138, 147, 0.08)" }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0]?.payload;
              const formatted = formatVolumeMetricValue(metric, row?.[metric], unitSystem);
              return (
                <ProgressChartTooltip
                  active
                  payload={payload}
                  label={row?.range_label || row?.label}
                  rows={formatted != null ? [{ label: metricLabel || metric, value: formatted }] : []}
                />
              );
            }}
          />
          <Bar
            dataKey={metric}
            fill={barColor}
            radius={[3, 3, 0, 0]}
            isAnimationActive={false}
            shape={(props) => {
              const { x, y, width, height, index } = props;
              const isLatest = index === latestIndex;
              return (
                <rect
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  fill={barColor}
                  stroke={isLatest ? CHART.hoverLine : "none"}
                  strokeWidth={isLatest ? 1.5 : 0}
                  rx={3}
                  ry={3}
                />
              );
            }}
          />
        </BarChart>
      </ProgressChartContainer>
    </div>
  );
};
